import { Component, ElementRef, OnInit, inject, ViewEncapsulation } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpResponse } from '@angular/common/http';
import { TdrsHttpEnvironmentService } from '../../service/environment';
import { StaticContentService } from '../../service/features/static-content.service';
import { isValidURL, utilGet } from '../../service/library/library';
import { FILE_TYPES, WARNINGS } from '../../app.constant';
import { downloadFile } from '../../common/file-download';
import { PreviewPopupService } from '../../../app/feature/preview-popup/preview-popup.service';

interface HelpCenterResponse {
  body?: {
    statusCode: number;
    status: string;
    data: string;
    message: string;
  }
}

@Component({
    selector: 'dd-helpcenter',
    templateUrl: './helpcenter.component.html',
    styleUrls: ['./helpcenter.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HelpcenterComponent implements OnInit {
  public helpcentercontent;
  public helpData;
  public helpDataWarning;
  public showSpinner = false;
  imageDataUrl: string;
  mediaType: string;
  previewUrl: string;

  public sanitizer = inject(DomSanitizer);
  public environmentService = inject(TdrsHttpEnvironmentService);
  public elementRef = inject(ElementRef);
  
  private readonly previewPopupService = inject(PreviewPopupService);
  private readonly staticContentService = inject(StaticContentService);

  ngOnInit() {
    this.getHelpData();
  }

  getHelpData() {
    this.showSpinner = true;
    this.staticContentService.getHelpCenterData()
      .subscribe({
        next: (res: HelpCenterResponse) => {
          if (res.body.statusCode === 200) {
            this.showSpinner = false;
            
            // Get the content
            let content = utilGet(res, 'body.data', '');
            
            // Replace the PDF path with the API URL
            // S5852: Use simple string replace instead of regex to avoid ReDoS
            const pdfPath = '/ns/resources/Project-Review-User-Guide-8.23.20.pdf';
            const replacementUrl = 'https://' + this.environmentService.api.url + '/api/v1/review/guidelines/FAQs';
            
            // Simple split and join - no regex, no backtracking, O(n) complexity
            content = content.split(pdfPath).join(replacementUrl);
            
            // Replace href1 with data-apiurl
            // S5852: Use split/join instead of regex
            content = content.split('href1=').join('data-apiurl=');
            
            // Remove Angular event bindings
            // S5852: Use ReDoS-safe approach
            content = this.removeEventBindings(content);
            content = this.removePropertyBindings(content);
            
            // Set the content
            this.helpData = content;
            
            // Add click listener after DOM updates
            setTimeout(() => {
              this.addClickListener();
            }, 200);
          } else {
            this.displayWarning(res.body.message);
          }
        },
        error: (err) => {
          console.error('Error loading help data:', err);
          this.displayWarning(WARNINGS.UnknownError);
        }
      });
  }

  /**
   * Remove Angular event bindings like (click)="..."
   * S5852: Avoid ReDoS by using character-by-character parsing instead of regex
   */
  private removeEventBindings(content: string): string {
    let result = '';
    let i = 0;
    const len = content.length;
    
    while (i < len) {
      // Look for opening parenthesis
      if (content[i] === '(') {
        // Find the matching closing parenthesis for the binding name
        let j = i + 1;        
        // Read until we find )
        while (j < len && content[j] !== ')') {          
          j++;
        }
        
        // If we found a valid binding like (click)
        if (j < len && content[j] === ')') {
          // Check if followed by = and quotes
          let k = j + 1;
          
          // Skip whitespace
          while (k < len && (content[k] === ' ' || content[k] === '\t')) {
            k++;
          }
          
          // If followed by =
          if (k < len && content[k] === '=') {
            k++;
            
            // Skip whitespace
            while (k < len && (content[k] === ' ' || content[k] === '\t')) {
              k++;
            }
            
            // If followed by quote
            if (k < len && content[k] === '"') {
              const quoteChar = content[k];
              k++;
              
              // Find closing quote
              while (k < len && content[k] !== quoteChar) {
                if (content[k] === '\\' && k + 1 < len) {
                  k += 2; // Skip escaped character
                } else {
                  k++;
                }
              }
              
              if (k < len && content[k] === quoteChar) {
                k++; // Skip closing quote
              }
              
              // We found a complete event binding, skip it
              i = k;
              continue;
            }
          }
        }
      }
      
      // Not an event binding, keep this character
      result += content[i];
      i++;
    }
    
    return result;
  }

  /**
   * Remove Angular property bindings like [property]="..."
   * S5852: Avoid ReDoS by using character-by-character parsing
   */
  private removePropertyBindings(content: string): string {
    let result = '';
    let i = 0;
    const len = content.length;
    
    while (i < len) {
      // Look for opening bracket
      if (content[i] === '[') {
        // Find the matching closing bracket
        let j = i + 1; 
        
        // Read until we find ]
        while (j < len && content[j] !== ']' && content[j] !== ' ' && content[j] !== '>') {          
          j++;
        }
        
        // If we found a valid binding like [property]
        if (j < len && content[j] === ']') {
          // Check if followed by = and quotes
          let k = j + 1;
          
          // Skip whitespace
          while (k < len && (content[k] === ' ' || content[k] === '\t')) {
            k++;
          }
          
          // If followed by =
          if (k < len && content[k] === '=') {
            k++;
            
            // Skip whitespace
            while (k < len && (content[k] === ' ' || content[k] === '\t')) {
              k++;
            }
            
            // If followed by quote
            if (k < len && content[k] === '"') {
              const quoteChar = content[k];
              k++;
              
              // Find closing quote
              while (k < len && content[k] !== quoteChar) {
                if (content[k] === '\\' && k + 1 < len) {
                  k += 2; // Skip escaped character
                } else {
                  k++;
                }
              }
              
              if (k < len && content[k] === quoteChar) {
                k++; // Skip closing quote
              }
              
              // We found a complete property binding, skip it
              i = k;
              continue;
            }
          }
        }
      }
      
      // Not a property binding, keep this character
      result += content[i];
      i++;
    }
    
    return result;
  }

  private addClickListener(): void {
    // Get the container where help content is rendered
    const container = this.elementRef.nativeElement.querySelector('.card-style');
    
    if (!container) {      
      return;
    }

    // Add single click listener to the container (event delegation)
    container.addEventListener('click', (event: Event) => {      
      this.handleContentClick(event);
    }, false);
  }

  private handleContentClick(event: Event): void {
    const target = event.target as HTMLElement;  
    
    // Handle image clicks
    if (target.tagName === 'IMG') {
      const alt = target.getAttribute('alt');      
      
      if (alt === 'Review Leader Instructions') {
        event.preventDefault();
        this.openVideoPreview(
          '/assets/images/static-content/ns/resources/video-poster1.png',
          'assets/images/static-content/ns/resources/Review-Leader-Training-Video-10.14.22.mp4'
        );
      } else if (alt === 'Agency Instructions') {
        event.preventDefault();
        this.openVideoPreview(
          '/assets/images/static-content/ns/resources/video-poster2.png',
          'assets/images/static-content/ns/resources/Agency-Training-Video-7.18.22.mp4'
        );
      } else if (alt === 'How to Submit Videos') {
        event.preventDefault();
        this.openVideoPreview(
          '/assets/images/static-content/ns/resources/video-poster3.png',
          'assets/images/static-content/ns/resources/How-to-Submit-Videos-7.18-22.mp4'
        );
      } else if (alt === 'User Guide Cover') {
        // Handle user guide cover click - find parent link
        this.handleUserGuideClick(event);
      }
    }
    
    // Handle link clicks with data-apiurl attribute
    if (target.tagName === 'A') {
      const dataApiUrl = (target as HTMLAnchorElement).dataset.apiurl;
      
      if (dataApiUrl) {
        event.preventDefault();
        this.downloadFromAPI(dataApiUrl);
      } else {
        console.warn('Link clicked but no data-apiurl attribute found');
        console.log('Link attributes:', Array.from(target.attributes).map(a => `${a.name}="${a.value}"`));
      }
    }
    
    // Handle clicks on elements inside links
    if (target.tagName === 'SPAN' || target.tagName === 'BR') {
      let parent = target.parentElement;
      let attempts = 0;
      
      while (parent && attempts < 5) {
        if (parent.tagName === 'A') {
          const dataApiUrl = (parent as HTMLAnchorElement).dataset.apiurl;     
          
          if (dataApiUrl) {
            event.preventDefault();
            this.downloadFromAPI(dataApiUrl);
          }
          return;
        }
        parent = parent.parentElement;
        attempts++;
      }
    }
  }

  private handleUserGuideClick(event: Event): void {
    event.preventDefault();
    
    // Try to find the parent <a> tag with data-apiurl
    let element = event.target as HTMLElement;
    let attempts = 0;
    const maxAttempts = 5;
    
    while (element && attempts < maxAttempts) {
      console.log('Checking element:', element.tagName, element);
      
      if (element.tagName === 'A') {
        const dataApiUrl = (element as HTMLAnchorElement).dataset.apiurl;
        
        if (dataApiUrl) {
          this.downloadFromAPI(dataApiUrl);
          return;
        }
      }
      element = element.parentElement;
      attempts++;
    }
    
    const fallbackUrl = 'https://' + this.environmentService.api.url + '/api/v1/review/guidelines/FAQs';
    this.downloadFromAPI(fallbackUrl);
  }

  private openVideoPreview(posterUrl: string, videoUrl: string): void {
    console.log('Opening video preview:', videoUrl);
    this.imageDataUrl = posterUrl;
    this.mediaType = 'video';
    this.previewUrl = videoUrl;
    this.previewPopupService.open(this.imageDataUrl, this.mediaType, this.previewUrl);
  }

  downloadFromAPI(url: string) {    
    if (isValidURL(url)) {
      this.showSpinner = true;
      const updatedUrl = 'HelpCenter/Project-Review-User-Guide-8.23.20.pdf';
      
      this.staticContentService
        .downLoadFiles(updatedUrl)
        .pipe(
          finalize(() => { 
            this.showSpinner = false;
          })
        )
        .subscribe({
          next: (res: HttpResponse<any>) => {
            downloadFile(res.body, 'user_guide.pdf', FILE_TYPES.PDF);
          },
          error: (err) => {
            console.error('Download failed:', err);
            this.displayWarning('Failed to download file');
          },
        });
    } else {
      console.error('Invalid URL, download cancelled');
      this.displayWarning('Invalid download URL');
    }
  }

  displayWarning(errorMessage: string) {
    this.showSpinner = false;
    this.helpDataWarning = errorMessage;
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      globalThis.print();
    }
  }
}